import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

type Options = {
  delay?: number;
  replace?: boolean;
  clearPagination?: boolean;
};

export function useDebouncedQueryParam(
  key: string,
  { delay = 400, replace = true, clearPagination = true }: Options = {}
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const paramValue = searchParams.get(key) ?? '';
  const [value, setValue] = useState(paramValue);

  useEffect(() => {
    setValue(paramValue);
  }, [paramValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const normalized = value.trim();

      if (normalized === paramValue) return;

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          if (normalized) next.set(key, normalized);
          else next.delete(key);

          if (clearPagination) next.delete('page');
          if (clearPagination) next.delete('limit');

          return next;
        },
        { replace, viewTransition: false }
      );
    }, delay);

    return () => clearTimeout(timeout);
  }, [value, paramValue, key, delay, setSearchParams]);

  return {
    value,
    setValue,
    paramValue,
  };
}
